import React, { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload } from 'lucide-react'
import { tr } from 'i18n'
import styles from './LocalizedFileInput.module.scss'

const LocalizedFileInput = forwardRef(function LocalizedFileInput({
  onChange, className = '', style, multiple = false, disabled = false, id,
  'aria-label': accessibleLabel, ...inputProps
}, forwardedRef) {
  const { i18n } = useTranslation()
  const generatedId = useId()
  const inputId = id || `file-${generatedId}`
  const namesId = `${inputId}-names`
  const nativeInput = useRef(null)
  const [names, setNames] = useState([])
  useImperativeHandle(forwardedRef, () => nativeInput.current)

  const buttonText = tr(multiple ? 'Выбрать файлы' : 'Выбрать файл')
  const label = accessibleLabel || buttonText
  const selectionText = names.length ? names.join(', ') : tr(multiple ? 'Файлы не выбраны' : 'Файл не выбран')

  useEffect(() => {
    nativeInput.current?.setCustomValidity(inputProps.required && !names.length ? buttonText : '')
  }, [buttonText, inputProps.required, names.length, i18n.language])

  function handleChange(event) {
    onChange?.(event)
    setNames(Array.from(event.target.files || [], file => file.name))
  }

  return (
    <span className={`${styles.root} ${className}`} style={style ? { ...style, display: 'flex' } : undefined}>
      <button
        type="button"
        className={styles.choose}
        disabled={disabled}
        aria-label={tr(label)}
        aria-controls={inputId}
        aria-describedby={namesId}
        onClick={() => nativeInput.current?.click()}
      >
        <Upload size={15} aria-hidden="true" />
        {buttonText}
      </button>
      <input
        {...inputProps}
        id={inputId}
        ref={nativeInput}
        type="file"
        multiple={multiple}
        disabled={disabled}
        aria-label={tr(label)}
        aria-describedby={namesId}
        onChange={handleChange}
        tabIndex={-1}
        className={styles.input}
      />
      <span id={namesId} className={styles.selection} title={names.join(', ')} aria-live="polite">
        {selectionText}
      </span>
    </span>
  )
})

export default LocalizedFileInput
